import React, { useState, useEffect } from 'react';
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Button, 
    Typography, 
    Box, 
    Checkbox, 
    FormControlLabel, 
    List, 
    ListItem, 
    ListItemText, 
    Divider,
    Paper,
    Chip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const ContextSyncModal = ({ 
    open, 
    onClose, 
    matches, 
    newTranslation, 
    onReplaceSelected 
}) => {
    const [selectedIndices, setSelectedIndices] = useState([]);

    useEffect(() => {
        if (open && matches) {
            setSelectedIndices(matches.map(m => m.index));
        }
    }, [open, matches]);

    const handleToggle = (index) => {
        setSelectedIndices(prev => 
            prev.includes(index) 
                ? prev.filter(i => i !== index) 
                : [...prev, index]
        );
    };

    const handleSelectAll = () => {
        if (selectedIndices.length === matches.length) {
            setSelectedIndices([]);
        } else {
            setSelectedIndices(matches.map(m => m.index));
        }
    };

    const handleConfirm = () => {
        onReplaceSelected(selectedIndices);
        onClose();
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="md" 
            fullWidth
            PaperProps={{
                sx: { borderRadius: '16px' }
            }}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight={700}>Review Matches</Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                         <Typography variant="caption" color="text.secondary">
                            {selectedIndices.length} of {matches.length} selected
                        </Typography>
                        <Button size="small" onClick={handleSelectAll}>
                            {selectedIndices.length === matches.length ? 'Deselect All' : 'Select All'}
                        </Button>
                    </Box>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ mb: 3, p: 2, bgcolor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                        New Translation to Apply:
                    </Typography>
                    <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                        {newTranslation}
                    </Typography>
                </Box>

                <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                    {matches.map((match) => (
                        <React.Fragment key={match.index}>
                            <ListItem 
                                alignItems="flex-start"
                                secondaryAction={
                                    <Checkbox
                                        edge="end"
                                        onChange={() => handleToggle(match.index)}
                                        checked={selectedIndices.includes(match.index)}
                                    />
                                }
                                sx={{ 
                                    py: 2,
                                    '&:hover': { bgcolor: '#f1f5f9' },
                                    borderRadius: '8px',
                                    mb: 1
                                }}
                            >
                                <ListItemText
                                    primary={
                                        <Box sx={{ mb: 1 }}>
                                            <Chip 
                                                label={`Segment #${match.index + 1}`} 
                                                size="small" 
                                                sx={{ height: 18, fontSize: '0.65rem', mb: 1 }} 
                                            />
                                            <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                                                {match.source}
                                            </Typography>
                                        </Box>
                                    }
                                    secondary={
                                        <Box component="span">
                                            <Typography
                                                component="span"
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: 'block' }}
                                            >
                                                Current Translation:
                                            </Typography>
                                            <Typography
                                                component="span"
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontStyle: 'italic' }}
                                            >
                                                {match.currentTranslation || '(Empty)'}
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </ListItem>
                            <Divider component="li" />
                        </React.Fragment>
                    ))}
                </List>
            </DialogContent>
            <DialogActions sx={{ p: 2.5 }}>
                <Button onClick={onClose} color="inherit">Cancel</Button>
                <Button 
                    onClick={handleConfirm} 
                    variant="contained" 
                    disabled={selectedIndices.length === 0}
                    startIcon={<CheckCircleIcon />}
                    sx={{ borderRadius: '8px', px: 3 }}
                >
                    Replace Selected
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ContextSyncModal;
